import React, { useState, useMemo, useEffect } from 'react';
import { RiHomeOfficeFill } from 'react-icons/ri';
import { BsBuilding,BsCalendar2Check, BsSearch, BsClock } from 'react-icons/bs';
import { TbZoomReset } from 'react-icons/tb';
import { useForm } from "react-hook-form";
import { useCash } from "../../../../hooks/useCash";
import DataTable from '../../../../components/ui/DataTable';
import Spinner from "../../../../components/Spinner";
import { getUser, formatCurrency, sqlDateFormat} from '../../../../utils/Common';
import SelectSearch from '../../../../components/ui/SelectSearch';
import NotifyC from '../../../../components/Notifications';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TimeRangePicker from 'rsuite/TimeRangePicker';
import CustomProvider  from 'rsuite/CustomProvider';
import * as locales from 'rsuite/locales';
import 'rsuite/TimeRangePicker/styles/index.css';
import './index.css';

const SearchCashBox = () => {
    const { getCashBoxStatus, getAllCashBoxMovements } = useCash();
    const { handleSubmit, reset, errors } = useForm();
    const user = getUser();
    const [cashBoxes, setCashBoxes] = useState([]);
    const [listCashBox, setListCashBox] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [cashBoxSelected, setCashBoxSelected] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [time, setTime] = useState([]);
    const [start, end] = Array.isArray(time) ? time : [new Date(), new Date()];
    const data = Object.keys(locales).map(key => ({
        key: key.replace(/([a-z]{2})([A-Z]{2})/, '$1-$2'),
        value: locales[key]
      }));
    const [localeKey, setLocaleKey] = useState('es-ES');
    const locale = data.find(item => item.key === localeKey);

    const handleGetListCashBoxes = async () => {
        const abortController = new AbortController();
        const response = await getCashBoxStatus(abortController.signal);
        if (response?.cajas?.length > 0){
            setListCashBox(response.cajas);
        }
    };

    const resetForm = () => {
        reset();
        setCashBoxSelected("");
        setDateRange([null, null]);
        setCashBoxes([]);
        setHasSearched(false);
        setTime([]);
    };

    const handleOnSubmit = async () => {

        if (!cashBoxSelected) {
            NotifyC.error("Debes seleccionar una caja.");
            return;
        }

        if (!startDate && !endDate && start && end) {
            NotifyC.error("Por favor, selecciona un rango de fechas."); 
            return;
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            NotifyC.error("La fecha 'Desde' no puede ser mayor que la fecha 'Hasta'.");
            return;
        }

        if (start && end && formatTime(start) > formatTime(end)) {
            NotifyC.error("La hora 'Desde' no puede ser mayor que la hora 'Hasta'.");
            return;
        }

        const dataToSend = {
            IdCashBox: cashBoxSelected,
            type: '1,2,3,4,5',
            fechaDesde: startDate ? startDate : null,
            fechaHasta: endDate ? endDate : null,
            horaDesde: start ? formatTime(start): null,
            horaHasta: end? formatTime(end) : null,
        };
        
        setIsLoading(true);
    
        try {
            const abortController = new AbortController();
            const response = await getAllCashBoxMovements(abortController.signal, dataToSend);
    
            if (response?.code === 200) {
                const updatedInfoCashBox = response.payload.map((mov) => {
                    return {
                        id: mov.IdSucursal || 0,
                        code: mov.Codigo || "",
                        name: mov.NombreSucursal || "",
                        nameCaja: mov.NombreCaja || "",
                        nameCode: (mov.Codigo || "") + ' ' + (mov.NombreSucursal || ""),
                        IdTipoMovimiento: mov.IdTipoMovimiento || 0,
                        FechaMovimientoCaja: mov.FechaMovimientoCaja || "",
                        MontoMovimiento: mov.MontoMovimiento || 0,
                        saldoAsignado: mov.IdTipoMovimiento === 3 ? mov.MontoMovimiento : 0,
                        saldoRetirado: mov.IdTipoMovimiento === 4 ? mov.MontoMovimiento : 0,
                        saldoAnulado: mov.IdTipoMovimiento === 5 ? mov.MontoMovimiento : 0,
                        SaldoFinalCaja: (mov.IdTipoMovimiento === 1 || mov.IdTipoMovimiento === 2) ? mov.SaldoCaja : mov.SaldoFinalCaja,
                    };
                });
    
                NotifyC.success(`Movimientos encontrados.`);
                setCashBoxes(updatedInfoCashBox || []);
                setHasSearched(true);
            } else {
                if (response?.message === "CASHBOX_MOVEMENTS_NOT_FOUND") {
                    NotifyC.error("No se encontraron movimientos para la caja seleccionada.");
                }
            }
        } catch (error) {
            console.error(error);
            NotifyC.error("No se pudieron cargar los datos. Por favor, inténtalo de nuevo.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = (name, value) => {
        if (name === "cashBox") {
            setCashBoxSelected(value);
        }
    };

    const formatTime = (date) => {
    
        if (!(date instanceof Date) || isNaN(date)) {
            return null; 
        }
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    const handleNameMovimientos = (id) => {
        if (id === undefined) return ""; 
        switch (id) {
            case 1: return "Apertura de caja";
            case 2: return "Cierre de caja";
            case 3: return "Saldo Asignado";
            case 4: return "Saldo Retirado";
            case 5: return "Saldo Anulado";
            default: return "";
        }
    };

    const renderOriginSelectCashBox = (nameInput, title) => {
        const options = listCashBox.map(cashBox => ({
            value: cashBox.IdCaja || "",
            label: cashBox.NombreSucursal + ' - ' + cashBox.NombreCaja || ""
        }));
    
        return (
            <div className="col-12 col-md-6 input-group text-left mb-3" style={{  width: "100%" }}>
                <div className="d-flex w-100">
                    <div className="input-group-prepend d-flex align-items-center">
                        <span className="input-group-text text-bold input-label" style={{ backgroundColor: "#FBD825", color: "black", borderRadius: "6px 0 0 6px" }}>
                            <BsBuilding className="mr-2" size={18} /> {title}
                        </span>
                    </div>
                    <SelectSearch 
                        options={options} 
                        nameInput={nameInput} 
                        setValue={handleSelect} 
                        value={cashBoxSelected} 
                    />
                </div>
            </div>
        );
    };

    const columns = useMemo(() => [
        { Header: "Fecha", accessor: "FechaMovimientoCaja", id: "fecha", Cell: ({ value }) => value ? sqlDateFormat(value, false, true).split('-')[0] : "",  exportable: true },
        { Header: "Hora", accessor: "FechaMovimientoCaja", id: "hora", Cell: ({ value }) => value ? sqlDateFormat(value, false, true).split('-')[1] : "", exportable: true },
        { Header: "Caja", accessor: "nameCaja", exportable: true },
        { Header: "Movimiento", accessor: "IdTipoMovimiento", Cell: ({ value }) => handleNameMovimientos(value), exportable: true },
        { Header: "Monto", accessor: "MontoMovimiento", Cell: ({ value }) => formatCurrency(value || 0), exportable: true,isTotalizable: true },
        { Header: "Saldo de caja", accessor: "SaldoFinalCaja", Cell: ({ value }) => formatCurrency(value), exportable: true,isTotalizable: true },
    ], []);

    useEffect(() => {
        handleGetListCashBoxes();
        
    }, []);

    return (
        <>
            <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
                <div className="d-flex justify-content-between" style={{ gap: 20 }}>
                    <div className="d-flex align-items-center">
                        <RiHomeOfficeFill size={20} className="mr-2" />
                        <h5 className="mb-0">Búsqueda de Caja</h5>
                    </div>
                </div>
            </div>
            <div className="dashboardCard p-3" style={{ color: "black" }}>
                <div className="d-flex justify-content-between flex-wrap mb-2">
                    <p className="mb-0">Filtros de búsqueda</p>
                </div>
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                    <div className="row" style={{ flex: 0.5}}>
                        {renderOriginSelectCashBox("cashBox", "Caja")}
                        <div className="col-12 col-md-6 input-group text-left mb-3 customDatePickerWidth" style={{ width: "100%" }}>
                            <div className="input-group-prepend d-flex align-items-center">
                                <span className="input-group-text text-bold input-label" style={{ backgroundColor: "#FBD825", color: "black" }}>
                                    <BsCalendar2Check className="mr-2" size={18} /> Rango de Fechas
                                </span>
                            </div>
                            <DatePicker
                                type="text"
                                selectsRange={true}
                                startDate={startDate}
                                endDate={endDate}
                                onChange={(update) => setDateRange(update)}
                                name="dateRange"
                                isClearable={true}
                                placeholderText="Selecciona un rango de fechas"
                                className="form-control input-field "
                                dateFormat="dd/MM/yyyy"
                                style={{ flexGrow: 1, width: "100%", minWidth: "300px" }}
                            />
                        </div>
                             {/* Nuevo selector de rango de horas */}
                   

                        <div className="col-12 col-md-6 input-group text-left mb-3"style={{ width: "100%" }} >
                            <div className="input-group-prepend d-flex align-items-center ">
                                <span className="input-group-text text-bold input-label pb-1" style={{ backgroundColor: "#FBD825", color: "black" }}>
                                    <BsClock className="mr-2" size={18} /> Rango de Horas
                                </span>
                            </div>
                           <CustomProvider locale={locale.value}>
                             <TimeRangePicker 
                                format="hh:mm aa"
                                showMeridiem 
                                className="input-field w-100 "
                                value={time}
                                onChange={setTime}
                                placeholder="Selecciona un rango de horas"
                            />
                            </CustomProvider>
                        </div>
                   
                    </div>
                   <div className="d-flex justify-content-center mt-3" style={{ gap: "12px" }}>
                        {isLoading ? (
                            <button type="submit" className="btn btn-blue" disabled> Buscar</button>
                        ) : (
                            <button type="submit" className="btn btn-blue"><BsSearch /> Buscar</button>
                        )}
                        <button type="reset" className="btn btn-secondary" onClick={resetForm}><TbZoomReset size={20} /> Reiniciar</button>
                    </div>
                </form>
            </div>

            {hasSearched && cashBoxes.length > 0 && !isLoading && (
                <div className="dashboardCard p-3" style={{ color: "black" }}>
                    <DataTable
                        columns={columns}
                        data={cashBoxes}
                        tableTitle="Resultados de la búsqueda"
                        filename="reporte-cajas"
                        exportExcel
                        exportPdf
                        exportableColumns={columns.filter(column => column.exportable)}
                        nameReport='REPORTE CAJA'
                        includeTotals={true}
                        sucursalSelected={cashBoxes[0]?.nameCode}
                        logTypeExcel={7}
                        logTypePdf={11}
                        IdCaja={cashBoxSelected}
                    />
                </div>
            )}

            {isLoading && (
                <div className="dashboardCard d-flex justify-content-center align-items-center p-3" style={{ color: "black" }}>
                <div className="text-center ">
                     <Spinner type={2} size={40} color="black" />
                    </div>
                </div>
              
            )}
        </>
    );
};

export default SearchCashBox;